import { Box, Grid, Center, useBreakpointValue, Text, Image, Stack, useColorModeValue, Button } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TypingText } from "./TypingText";
import type { DealFinderContentProps } from "../../../types/dealfinder";
import { DealTile } from "./DealTile";
import { DealSkeletonTile } from "./DealSkeletonTile";
import { useDealFinderTickers } from "../../../hooks/useDealFinderTickers";
import type { DealFinderTicker } from "../../../api/tickers/models/DealFinderTicker";
import type { TickerType } from "../../../api/tickers/models/TickerType";
import { useConversationContext } from "../../thread/ConversationContext";
import { useConversation, useConversationDataTickerSymbols } from "hooks/useConversation";
import { useSidebarNavigation, useInvestmentDetailModal, useEntitlementKey, useButtonProps, useUserPreference } from "hooks";
import { useCopyValue } from "hooks/useCopies";
import { getWebsocketClient } from "api/websocket/client";
import type { TopicSubscription } from "api/websocket/TopicSubscription";
import { useIsWebsocketConnected } from "hooks/useWebsocket";
import { useInfiniteLoading } from "../../../hooks/useInfiniteLoading";
import deal_loader from "screens/common/static/images/deal_loader.gif";
import { useDealFinderContext } from "../../thread/DealFinderContext";

export const DealFinderContent: React.FC<DealFinderContentProps> = ({ onResetInput }) => {
  const [deals, setDeals] = useState<DealFinderTicker[]>([]);
  const [visibleTileCount, setVisibleTileCount] = useState(0);
  const noResultsMessage = useCopyValue("copy_deal_no_results_message");
  const resetSearchButtonText = useCopyValue("copy_deal_reset_search_button");
  const dealFinderLoadingMessages = useCopyValue("copy_deal_finder_loading_messages");
  const { currentDealId } = useInvestmentDetailModal();
  const [dealsMap, setDealsMap] = useState<Map<string, DealFinderTicker>>(new Map());
  const loadedIdsRef = useRef<Set<string>>(new Set());
  const [isConversationCompleted, setIsConversationCompleted] = useState(false);
  const { getDealFinderTickers } = useDealFinderTickers();
  // Get default deals from context instead of local state
  const { defaultDeals, isDefaultDealsLoaded, isLoadingDefaultDeals } = useDealFinderContext();
  const [isInitialOrReset, setIsInitialOrReset] = useState(true);
  const {
    conversationId,
    setConversationId,
    setShouldClearInputValue,
    shouldClearInputValue,
    setIsRequesting,
    isRequesting,
    activeFilter,
    setActiveFilter,
    activeFilterUrlKey,
    setActiveFilterUrlKey,
  } = useConversationContext();
  const { conversationState } = useConversation(conversationId);
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { fallback: "md", ssr: false });
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { fallback: "md", ssr: false });
  const { currentSidebarType } = useSidebarNavigation();
  const exchanges = useCopyValue("copy_ticker_exchange_options");
  const isWebsocketConnected = useIsWebsocketConnected();
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const bgColorLoader = useColorModeValue("white", "gray.700");
  const [validTickerSymbols, setValidTickerSymbols] = useState<string[]>([]);
  const hasDealFinderPurchase = useEntitlementKey("ui_enable_dealfinder_purchase");
  const primaryButtonProps = useButtonProps("sm", "primary");
  const showFullWidth = useUserPreference("ui_enable_view_full_width") as boolean;
  const columns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 }) || 1;

  const memoizedTexts = useMemo(() => {
    return [
      hasDealFinderPurchase ? "Looking for deals..." : "Searching for stocks...",
      ...(dealFinderLoadingMessages.length > 0 ? dealFinderLoadingMessages : []),
    ];
  }, [hasDealFinderPurchase, dealFinderLoadingMessages]);

  const getPrivateExchanges = useCallback(() => {
    return exchanges.filter((exch) => exch.toLowerCase().startsWith("private"));
  }, [exchanges]);

  useEffect(() => {
    if (
      !conversationId ||
      conversationState === "complete" ||
      conversationState === "action" ||
      conversationState === "error" ||
      conversationState === "unknown"
    ) {
      setIsRequesting(false);
      setTimeout(() => {
        setIsConversationCompleted(true);
      }, 500);
    } else {
      setIsConversationCompleted(false);
      setIsInitialOrReset(false);
    }
  }, [conversationId, conversationState, setIsRequesting]);

  useEffect(() => {
    if (isRequesting) {
      // Clear everything when a new query starts
      setDeals([]);
      setDealsMap(new Map());
      loadedIdsRef.current = new Set();
      setValidTickerSymbols([]);
      setIsInitialOrReset(false);
      setVisibleTileCount(0);
    }
  }, [isRequesting]);

  const resetDealFinderState = useCallback(() => {
    setConversationId("");
    setValidTickerSymbols([]);
    setIsInitialOrReset(true);
    setVisibleTileCount(0);

    // If we have default deals loaded, use them instead of clearing everything
    if (isDefaultDealsLoaded && defaultDeals.length > 0) {
      const newMap = new Map<string, DealFinderTicker>();
      defaultDeals.forEach((deal) => newMap.set(deal.id, deal));
      setDealsMap(newMap);
      setDeals(defaultDeals);
    } else {
      setDeals([]);
      setDealsMap(new Map());
    }

    setActiveFilter(null);
    setActiveFilterUrlKey(null);
    setIsRequesting(false);
  }, [setConversationId, isDefaultDealsLoaded, defaultDeals, setActiveFilter, setActiveFilterUrlKey, setIsRequesting]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        resetDealFinderState();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [resetDealFinderState]);

  useEffect(() => {
    if (shouldClearInputValue) {
      resetDealFinderState();
      setShouldClearInputValue(false);
    }
  }, [shouldClearInputValue, resetDealFinderState, setShouldClearInputValue]);

  const tickerSymbolsFromConversation = useConversationDataTickerSymbols(conversationId);

  useEffect(() => {
    if (tickerSymbolsFromConversation) {
      setValidTickerSymbols((prevSymbols) => {
        const uniqueSymbols = new Set([...prevSymbols, ...tickerSymbolsFromConversation]);
        return Array.from(uniqueSymbols) as string[];
      });
    }
  }, [tickerSymbolsFromConversation]);

  const PAGE_SIZE = 12;

  const {
    loading: isLoading,
    lastMessageObserverRef,
    clear: clearInfiniteLoading,
  } = useInfiniteLoading({
    loadItems: async (token: string | null) => {
      try {
        const newLimit = token ? parseInt(token) + PAGE_SIZE : PAGE_SIZE;
        const tickerType = activeFilter?.toLowerCase().replace(" ", "_") as TickerType;
        const isPrivateFilter = activeFilter?.toLowerCase() === "private";

        const loadedIds = loadedIdsRef.current;
        const unloadedIds = validTickerSymbols.filter((id) => !loadedIds.has(id));

        if (unloadedIds.length === 0 && !isInitialOrReset) {
          return { data: [], nextToken: null };
        }

        let tickers: DealFinderTicker[];

        if (isInitialOrReset && validTickerSymbols.length === 0 && isDefaultDealsLoaded && defaultDeals.length > 0) {
          tickers = defaultDeals;
        } else {
          tickers = await getDealFinderTickers({
            limit: newLimit,
            type: isPrivateFilter ? undefined : tickerType,
            exchange: isPrivateFilter ? getPrivateExchanges() : undefined,
            ids: unloadedIds,
          });
        }

        const newTickers = tickers.filter((ticker) => !loadedIds.has(ticker.id));

        if (newTickers.length === 0) {
          return { data: [], nextToken: null };
        }

        newTickers.forEach((ticker) => loadedIds.add(ticker.id));

        setDealsMap((prevMap) => {
          const newMap = new Map(prevMap);
          newTickers.forEach((ticker) => newMap.set(ticker.id, ticker));
          return newMap;
        });

        return {
          data: newTickers,
          nextToken: newTickers.length > 0 ? newLimit.toString() : null,
          totalCount: newLimit,
        };
      } catch (error: any) {
        console.error("Error loading deals:", error);
        throw error;
      }
    },
  });

  // Load specific deal if dealId is in URL
  useEffect(() => {
    if (currentDealId && !dealsMap.has(currentDealId) && !isLoading) {
      const loadDealById = async () => {
        try {
          const tickers = await getDealFinderTickers({
            ids: [currentDealId],
          });

          if (tickers.length > 0) {
            setDealsMap((prevMap) => {
              const newMap = new Map(prevMap);
              tickers.forEach((ticker) => newMap.set(ticker.id, ticker));
              return newMap;
            });
          }
        } catch (error) {
          console.error("Error loading deal by ID:", error);
        }
      };
      loadDealById();
    }
  }, [currentDealId, dealsMap, isLoading, getDealFinderTickers]);

  const handleDealRefresh = useCallback(
    async (dealId: string) => {
      if (!dealId) return;
      try {
        const updatedDeals = await getDealFinderTickers({ ids: [dealId] });
        if (updatedDeals.length > 0) {
          const updatedDeal = updatedDeals[0];
          setDealsMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.set(updatedDeal.id, updatedDeal);
            return newMap;
          });
        } else {
          setDealsMap((prevMap) => {
            const newMap = new Map(prevMap);
            if (newMap.has(dealId)) {
              newMap.delete(dealId);
              return newMap;
            }
            return prevMap;
          });
        }
      } catch (error) {
        console.error(`Error refreshing deal ${dealId} via WebSocket:`, error);
      }
    },
    [getDealFinderTickers]
  );

  useEffect(() => {
    // Only use default deals if we're in initial/reset state and validTickerSymbols is empty
    if (isInitialOrReset && validTickerSymbols.length === 0 && isDefaultDealsLoaded && defaultDeals.length > 0) {
      const newMap = new Map<string, DealFinderTicker>();
      defaultDeals.forEach((deal) => newMap.set(deal.id, deal));
      setDealsMap(newMap);
      setDeals(defaultDeals);
    }

    if (!isInitialOrReset && validTickerSymbols.length > 0) {
      clearInfiniteLoading();
    }
  }, [activeFilter, clearInfiniteLoading, validTickerSymbols, isDefaultDealsLoaded, defaultDeals, isInitialOrReset]);

  useEffect(() => {
    activeFilter && setIsInitialOrReset(false);
  }, [activeFilter]);

  useEffect(() => {
    if (activeFilterUrlKey) {
      setIsRequesting(true);
      const fetchFilteredDeals = async () => {
        try {
          const filteredDeals = await getDealFinderTickers({
            list: activeFilterUrlKey,
          });

          if (filteredDeals.length > 0) {
            const newMap = new Map<string, DealFinderTicker>();
            filteredDeals.forEach((deal) => newMap.set(deal.id, deal));
            setDealsMap(newMap);
            setDeals(filteredDeals);
            setActiveFilterUrlKey(null);
            setIsRequesting(false);
          } else {
            setDeals([]);
            setDealsMap(new Map());
          }
        } catch (error) {
          console.error("Error fetching filtered deals:", error);
        }
      };
      fetchFilteredDeals();
      setIsInitialOrReset(false);
    }
  }, [activeFilterUrlKey, getDealFinderTickers, setActiveFilterUrlKey, setConversationId, setIsRequesting]);

  const filterDeals = useCallback(
    (deals: DealFinderTicker[]) => {
      if (!activeFilter) return deals;
      return deals.filter((deal) => {
        if (activeFilter.toLowerCase() === "equity" && deal.type === "equity") {
          return true;
        }
        if (activeFilter.toLowerCase() === "etf" && deal.type === "etf") {
          return true;
        }
        if (activeFilter.toLowerCase() === "mutual funds" && deal.type === "mutual_funds") {
          return true;
        }
        if (activeFilter.toLowerCase() === "private" && deal.exchange?.toLowerCase().startsWith("private")) {
          return true;
        }
        return false;
      });
    },
    [activeFilter]
  );

  useEffect(() => {
    setDeals(Array.from(dealsMap.values()));
  }, [dealsMap]);

  useEffect(() => {
    const totalDeals = Array.from(dealsMap.values()).length;
    if (visibleTileCount < totalDeals) {
      const timer = setTimeout(() => {
        setVisibleTileCount((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [dealsMap, visibleTileCount]);

  useEffect(() => {
    let sub: TopicSubscription | undefined;

    if (isWebsocketConnected) {
      try {
        const wsClient = getWebsocketClient();
        sub = wsClient.subscribeToEvent<{ dealId: string }>("deals/refresh", (payload) => {
          if (payload && payload.dealId) {
            handleDealRefresh(payload.dealId);
          } else {
            console.warn("DealFinderContent: Received deals/refresh event without dealId", payload);
          }
        });
      } catch (error) {
        console.warn("DealFinderContent: Failed to subscribe to deals/refresh WebSocket event:", error);
      }
    }

    return () => {
      if (sub) {
        try {
          sub.unsubscribe();
        } catch (error) {
          console.warn("DealFinderContent: Error unsubscribing from deals/refresh:", error);
        }
      }
    };
  }, [isWebsocketConnected, handleDealRefresh]);

  const skeletonCount = useMemo(() => {
    if (isRequesting && deals.length === 0) {
      const minTotalTiles = 8;
      return Math.max(0, minTotalTiles - deals.length);
    }
    if (isConversationCompleted || isInitialOrReset) {
      return 0;
    }
    return 1;
  }, [isRequesting, deals.length, isConversationCompleted, isInitialOrReset]);

  const filteredDealsLength = filterDeals(Array.from(dealsMap.values())).length;
  if ((deals.length === 0 || filteredDealsLength === 0) && !isLoading && !isRequesting && !isLoadingDefaultDeals && !isInitialOrReset) {
    return (
      <Center minH="50vh">
        <Stack spacing={"1rem"} direction="column" alignItems="center" maxWidth={"30rem"}>
          <Text className="ch-deal-finder-no-results" fontSize="lg" color={textColor} textAlign={"center"}>
            {noResultsMessage}
          </Text>
          <Button
            className="ch-deal-finder-reset-button"
            {...primaryButtonProps}
            onClick={() => {
              resetDealFinderState();
              setShouldClearInputValue(true);
            }}>
            {resetSearchButtonText}
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Box
      position="relative"
      width="100%"
      mx="auto"
      pl={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" && !showFullWidth ? "0" : "1rem"}
      pr={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" && !showFullWidth ? "0" : "1rem"}>
      {((!isConversationCompleted && !isInitialOrReset) || (activeFilterUrlKey && deals.length <= 0)) && (
        <Stack
          zIndex={3}
          direction="row"
          spacing="1rem"
          position="absolute"
          px="2rem"
          py="1rem"
          borderRadius={"full"}
          boxShadow={"0px 10px 40px rgba(0, 0, 0, 0.3)"}
          bgColor={bgColorLoader}
          top={hasDealFinderPurchase ? "24.5rem" : "18.5rem"}
          left="50%"
          minW="18rem"
          transform="translate(-50%, -50%)"
          alignItems="center">
          <Image src={deal_loader} alt="loading" objectFit="cover" objectPosition="right center" height="2rem" />
          <TypingText fontSize={isMobile ? "md" : "lg"} color={textColor} fontWeight={600} texts={memoizedTexts} loop />
        </Stack>
      )}
      <Grid
        templateColumns={showFullWidth ? "repeat(auto-fit, minmax(250px, 1fr))" : `repeat(${columns}, 1fr)`}
        gap={{ base: 4, md: 6, lg: 8 }}
        width="100%"
        justifyContent="center"
        alignItems="flex-start">
        {filterDeals(Array.from(dealsMap.values()))
          .slice(0, visibleTileCount)
          .map((deal) => (
            <Box key={deal.id} height="auto" opacity={0} animation="fadeIn 0.5s ease-in forwards">
              <style>
                {`
                  @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                  }
                `}
              </style>
              <DealTile deal={deal} />
            </Box>
          ))}
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <Box key={`skeleton-${index}`} height="auto">
            <DealSkeletonTile />
          </Box>
        ))}
      </Grid>
      <Box
        height="100px"
        width="100%"
        className="deal-finder-observer"
        ref={lastMessageObserverRef}
        position="relative"
        zIndex={1}
        bgColor="transparent"
        mb={8}
      />
    </Box>
  );
};
